import { gql, useQuery } from '@apollo/client';
import {
	AutoAwesomeOutlined,
	Forum,
	ForumOutlined,
	HelpOutline,
	Home,
	HomeOutlined,
	Notifications,
	NotificationsOutlined,
	PeopleAlt,
	PeopleAltOutlined,
	SvgIconComponent,
} from '@mui/icons-material';
import { Query, QueryNotificationsArgs } from 'middleware-types';
import { useSession } from 'utils/session';
import { isDemo } from 'utils/utils';
import { EvolutionConnectLogoIcon } from '../icons';

// get the badge counts for notifications, user inbound connections etc.
const GET_PRIMARY_NAV_BADGE_COUNTS = gql`
	query GetPrimaryNavBadgeCounts(
		$limit: Int!
		$offset: Int!
		$markAllAsSeen: Boolean!
		$targetEntityId: String
	) {
		# Retrieve a count of all unseen notification this user can access
		notifications(limit: $limit, offset: $offset, markAllAsSeen: $markAllAsSeen) {
			unseenCount
		}

		# Retrieve a count of all unseen notifications for a given Notified User
		# that target a specific entity. For example, imagine the current user is an associate
		# for an org and has permission to view that org's inbound connection requests.
		# This query with targetEntityId set to that org's ID would provide the number of
		# inbound connection request notifications for that org that that user has not seen.
		entityNetworkRequestsNotifications: notifications(
			limit: $limit
			offset: $offset
			markAllAsSeen: $markAllAsSeen
			targetEntityId: $targetEntityId
			types: [ConnectionRequested]
		) {
			unseenCount
		}
	}
`;

// get the badge counts for org inbound connections etc.
const GET_SECONDARY_NAV_BADGE_COUNTS = gql`
	query GetSecondaryNavBadgeCounts(
		$limit: Int!
		$offset: Int!
		$markAllAsSeen: Boolean!
		$targetEntityId: String
	) {
		entityNetworkRequestsNotifications: notifications(
			limit: $limit
			offset: $offset
			markAllAsSeen: $markAllAsSeen
			targetEntityId: $targetEntityId
			types: [ConnectionRequested]
		) {
			unseenCount
		}
	}
`;

// Single hook to get badge counts for the notifications page and the user network page icons.
export const usePrimaryNavBadgeCounts = (targetUserId?: string) => {
	const { data } = useQuery<
		Pick<Query, 'notifications'> & {
			entityNetworkRequestsNotifications: Query['notifications'];
		},
		QueryNotificationsArgs
	>(GET_PRIMARY_NAV_BADGE_COUNTS, {
		fetchPolicy: 'cache-and-network',
		pollInterval: isDemo() ? 20000 : undefined,
		skip: !isDemo(),
		variables: {
			limit: 0,
			offset: 0,
			markAllAsSeen: false,
			targetEntityId: targetUserId,
		},
		onError: (e) => {
			// fail silently
			console.log(e);
		},
	});
	return {
		notificationsCount: data?.notifications.unseenCount ?? 0,
		connectionRequestsCount: data?.entityNetworkRequestsNotifications?.unseenCount ?? 0,
	};
};

// Hook to get badge counts for organization network page tabs.
export const useSecondaryNavBadgeCounts = (targetEntityId?: string) => {
	const { data } = useQuery<
		Pick<Query, 'notifications'> & {
			entityNetworkRequestsNotifications: Query['notifications'];
		},
		QueryNotificationsArgs
	>(GET_SECONDARY_NAV_BADGE_COUNTS, {
		fetchPolicy: 'cache-and-network',
		pollInterval: isDemo() ? 20000 : undefined,
		skip: !isDemo(),
		variables: {
			limit: 0,
			offset: 0,
			markAllAsSeen: false,
			targetEntityId: targetEntityId,
		},
		onError: (e) => {
			// fail silently
			console.log(e);
		},
	});
	return {
		connectionRequestsCount: data?.entityNetworkRequestsNotifications?.unseenCount ?? 0,
	};
};

export const BADGE_COUNT_MAX = 99;

// generate the navigation link data
export interface PrimaryNavLinkInfo {
	title: string;
	path?: string;
	onClick?: React.MouseEventHandler<HTMLElement>;
	icon: SvgIconComponent;
	activeIcon?: SvgIconComponent;
	isOpen?: boolean;
	badgeCount?: number;
	isExternal?: boolean;
	demoOnly?: true;
	socialUserOnly?: boolean;
	siteUserOnly?: boolean;
}

export const usePrimaryNavLinks = (
	notificationsOpen: boolean,
	onNotificationsClick: React.MouseEventHandler<HTMLElement>
) => {
	const { user } = useSession();
	const isSiteUser = Boolean(user.siteUserId);

	const { notificationsCount, connectionRequestsCount } = usePrimaryNavBadgeCounts(user.userId);

	const links: PrimaryNavLinkInfo[] = [
		{
			title: 'Home',
			path: '/',
			icon: HomeOutlined,
			activeIcon: Home,
			socialUserOnly: true,
		},
		{
			title: 'Network',
			path: '/app/network/connections',
			icon: PeopleAltOutlined,
			activeIcon: PeopleAlt,
			badgeCount: connectionRequestsCount,
			demoOnly: true,
			socialUserOnly: true,
		},
		{
			title: 'Conversations',
			path: '/app/conversations',
			icon: ForumOutlined,
			activeIcon: Forum,
			demoOnly: true,
			socialUserOnly: true,
		},
		{
			title: 'Notifications',
			onClick: onNotificationsClick,
			icon: NotificationsOutlined,
			activeIcon: Notifications,
			isOpen: notificationsOpen,
			badgeCount: notificationsCount,
			demoOnly: true,
		},
		{
			title: 'Releases',
			path: 'https://www.evolution.global/product-updates',
			icon: AutoAwesomeOutlined,
			isExternal: true,
		},
		{
			title: 'Help',
			path: 'https://filetrac.freshdesk.com/support/home',
			icon: HelpOutline,
		},
	];

	return links.filter((link) => {
		if (link.demoOnly && !isDemo()) return false;
		if (link.socialUserOnly && isSiteUser) return false;
		if (link.siteUserOnly && !isSiteUser) return false;
		return true;
	});
};

// the link info for the site admin link
export const siteAdminLinkInfo: PrimaryNavLinkInfo = {
	title: 'Site Administration',
	path: '/site',
	icon: EvolutionConnectLogoIcon as SvgIconComponent,
};
